import { useCallback } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import type { HTMLAttributes } from "react";
import type { SidebarAreaNode, SidebarDnd, SidebarProjectNode, SidebarSpaceNode } from "@/concepts/types";
import { parentOf, useDragView, useDropState, type DragItem, type DropData } from "./DeskDnd";

type Node = SidebarAreaNode | SidebarSpaceNode | SidebarProjectNode;

function itemOf(node: Node): Extract<DragItem, { type: "container" }> {
  if (!("kind" in node)) return { type: "container", kind: "area", id: node.area.id, parent: "areas", name: node.area.name };
  if (node.kind === "space")
    return { type: "container", kind: "space", id: node.space.id, parent: parentOf("space", node.space.areaId), name: node.space.name };
  return { type: "container", kind: "project", id: node.project.id, parent: parentOf("project", null, node.project.spaceId), name: node.project.name };
}

/** Sidebar rows reorder among siblings, spaces move between areas, and every row accepts dropped tasks. */
export const sidebarDnd: SidebarDnd = {
  useRow(node) {
    const item = itemOf(node);
    const id = `${item.kind}:${item.id}`;
    const drag = useDraggable({ id, data: item });
    const drop = useDroppable({ id, data: item as DropData });
    const setDrag = drag.setNodeRef;
    const setDrop = drop.setNodeRef;
    const setNodeRef = useCallback(
      (el: HTMLElement | null) => {
        setDrag(el);
        setDrop(el);
      },
      [setDrag, setDrop],
    );
    return {
      setNodeRef,
      setActivatorNodeRef: drag.setActivatorNodeRef,
      listeners: (drag.listeners ?? {}) as HTMLAttributes<HTMLElement>,
      isDragging: drag.isDragging,
      drop: useDropState(id),
    };
  },
  useNavTarget(navId) {
    const data: DropData = { type: "dest", id: "inbox", label: "Inbox" };
    const id = `nav:${navId}`;
    const d = useDroppable({ id, data, disabled: navId !== "inbox" });
    const state = useDropState(id);
    return { setNodeRef: d.setNodeRef, drop: state };
  },
  useTopLevelZone(enabled) {
    const v = useDragView();
    const spaceDragging = v.active?.type === "container" && v.active.kind === "space" && v.active.parent !== "top";
    const d = useDroppable({ id: "toplevel", data: { type: "toplevel" } satisfies DropData, disabled: !enabled || !spaceDragging });
    const state = useDropState("toplevel");
    return { setNodeRef: d.setNodeRef, drop: state, visible: enabled && spaceDragging };
  },
};
